// pages/superadmin/SuperAdminApp.jsx
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { LogOut, Users, Package, Building2, Shield, Plus, Edit2, Trash2, CheckCircle, XCircle, RefreshCw, ArrowRight, Database, Settings, User, Lock, KeyRound, Mail, LogIn, TrendingUp, Clock, AlertTriangle, UserX, Calendar, Wallet } from 'lucide-react';
import SuperAdminMigrations from './SuperAdminMigrations';
import SuperAdminSystem from './SuperAdminSystem';
import TotpChallengeForm from '../../components/TotpChallengeForm';
import TotpEnrollForm from '../../components/TotpEnrollForm';
import TotpBackupCodes from '../../components/TotpBackupCodes';
import BrandIcon from '../../components/BrandIcon';
// Modale de confirmation générique (remplace les window.confirm() natifs) —
// partagée avec TableOrdersPage.jsx, voir components/ConfirmModal.jsx.
import ConfirmModal from '../../components/ConfirmModal';
import StatCard from '../../components/StatCard';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:4000/api');
const DEVICE_TOKEN_KEY = 'sa_device_token';

const saApi = axios.create({ baseURL: API });
saApi.interceptors.request.use(cfg => {
  const t = localStorage.getItem('sa_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// ── Login (avec étape 2FA) ──────────────────────────────────────
function SALogin({ onLogin }) {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  // 'password' | 'totp' | 'forgot' | 'forgot-sent' | 'reset' | 'reset-done'
  const [step, setStep] = useState(() =>
    new URLSearchParams(window.location.search).get('reset') ? 'reset' : 'password'
  );
  const [challengeToken, setChallengeToken] = useState(null);
  const [totpError, setTotpError] = useState(null);

  // ── Mot de passe oublié ──────────────────────────────────────
  const [forgotEmail, setForgotEmail] = useState('');
  const resetToken = new URLSearchParams(window.location.search).get('reset');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await saApi.post('/superadmin/forgot-password', { email: forgotEmail });
      setStep('forgot-sent');
    } catch { toast.error('Une erreur est survenue, réessayez.'); }
    finally { setLoading(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) return toast.error('8 caractères minimum');
    if (newPassword !== newPasswordConfirm) return toast.error('Les mots de passe ne correspondent pas');
    setLoading(true);
    try {
      await saApi.post('/superadmin/reset-password', { token: resetToken, newPassword });
      setStep('reset-done');
    } catch(e) { toast.error(e.response?.data?.error || 'Lien invalide ou expiré'); }
    finally { setLoading(false); }
  };

  // Connexion sans mot de passe via clé d'accès — la 2FA n'est pas
  // redemandée (la clé combine déjà possession + inhérence).
  const submitPasskey = async () => {
    setPasskeyLoading(true);
    try {
      const { data: options } = await saApi.post('/superadmin/passkeys/login-options');
      const response = await startAuthentication(options);
      const r = await saApi.post('/superadmin/passkeys/login-verify', { response });
      localStorage.setItem('sa_token', r.data.token);
      onLogin(r.data.admin);
    } catch(e) {
      if (e?.name !== 'NotAllowedError') {
        toast.error(e.response?.data?.error || "Échec de la connexion par clé d'accès");
      }
    } finally { setPasskeyLoading(false); }
  };

  const submit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const deviceToken = localStorage.getItem(DEVICE_TOKEN_KEY) || undefined;
      const r = await saApi.post('/superadmin/login', { email, password, deviceToken });
      if (r.data.requiresTotp) {
        setChallengeToken(r.data.challengeToken);
        setStep('totp');
      } else {
        localStorage.setItem('sa_token', r.data.token);
        onLogin(r.data.admin);
      }
    } catch(e) { toast.error(e.response?.data?.error || 'Identifiants incorrects'); }
    finally { setLoading(false); }
  };

  const submitTotp = async (code, isBackupCode, remember) => {
    setTotpError(null);
    setLoading(true);
    try {
      const r = await saApi.post('/superadmin/totp/verify', { challengeToken, code, isBackupCode, remember });
      if (r.data.deviceToken) localStorage.setItem(DEVICE_TOKEN_KEY, r.data.deviceToken);
      localStorage.setItem('sa_token', r.data.token);
      onLogin(r.data.admin);
    } catch(e) { setTotpError(e.response?.data?.error || 'Code invalide'); }
    finally { setLoading(false); }
  };

  if (step === 'totp') {
    return (
      <TotpChallengeForm
        title="Double authentification"
        onSubmit={submitTotp}
        onBack={() => { setStep('password'); setTotpError(null); }}
        loading={loading}
        error={totpError}
      />
    );
  }

  const saCardStyle = { minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center',
    background:'#0a0b0f', fontFamily:'Inter, sans-serif' };
  const saInnerStyle = { width:360, padding:32, background:'#13141a', borderRadius:16,
    border:'1px solid #2a2b35', boxShadow:'0 20px 60px rgba(0,0,0,0.5)' };
  const saInputStyle = { width:'100%', padding:'10px 12px', background:'#1e1f28',
    border:'1px solid #2a2b35', borderRadius:8, color:'#fff', fontSize:14,
    outline:'none', boxSizing:'border-box' };
  const saLabelStyle = { display:'block', color:'#aaa', fontSize:12, fontWeight:600,
    textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 };

  if (step === 'forgot') {
    return (
      <div style={saCardStyle}>
        <div style={saInnerStyle}>
          <div style={{ textAlign:'center', marginBottom:24 }}>
            <h1 style={{ color:'#fff', fontSize:18, fontWeight:700, margin:0 }}>Mot de passe oublié</h1>
            <p style={{ color:'#666', fontSize:13, marginTop:6 }}>On vous envoie un lien de réinitialisation</p>
          </div>
          <form onSubmit={submitForgot}>
            <div style={{ marginBottom:20 }}>
              <label style={saLabelStyle}>Email</label>
              <input type="email" value={forgotEmail} onChange={e=>setForgotEmail(e.target.value)}
                required autoFocus style={saInputStyle}/>
            </div>
            <button type="submit" disabled={loading}
              style={{ width:'100%', padding:'11px', background:'#7c5cfc', border:'none',
                borderRadius:8, color:'#fff', fontSize:14, fontWeight:600, cursor:'pointer' }}>
              {loading ? 'Envoi...' : 'Envoyer le lien'}
            </button>
          </form>
          <button type="button" onClick={() => setStep('password')}
            style={{ display:'block', margin:'16px auto 0', background:'none', border:'none',
              color:'#888', textDecoration:'underline', cursor:'pointer', fontSize:13 }}>
            ← Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  if (step === 'forgot-sent') {
    return (
      <div style={saCardStyle}>
        <div style={{ ...saInnerStyle, textAlign:'center' }}>
          <div style={{ fontSize:28, marginBottom:10 }}>✓</div>
          <p style={{ color:'#aaa', fontSize:13, lineHeight:1.6, marginBottom:20 }}>
            Si un compte existe avec cet email, un lien de réinitialisation vient de lui être envoyé.
          </p>
          <button type="button" onClick={() => setStep('password')}
            style={{ width:'100%', padding:'11px', background:'#1e1f28', border:'1px solid #2a2b35',
              borderRadius:8, color:'#fff', fontSize:14, fontWeight:600, cursor:'pointer' }}>
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  if (step === 'reset') {
    return (
      <div style={saCardStyle}>
        <div style={saInnerStyle}>
          <div style={{ textAlign:'center', marginBottom:24 }}>
            <h1 style={{ color:'#fff', fontSize:18, fontWeight:700, margin:0 }}>Nouveau mot de passe</h1>
          </div>
          <form onSubmit={submitReset}>
            <div style={{ marginBottom:14 }}>
              <label style={saLabelStyle}>Nouveau mot de passe</label>
              <input type="password" value={newPassword} onChange={e=>setNewPassword(e.target.value)}
                required autoFocus style={saInputStyle}/>
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={saLabelStyle}>Confirmer</label>
              <input type="password" value={newPasswordConfirm} onChange={e=>setNewPasswordConfirm(e.target.value)}
                required style={saInputStyle}/>
            </div>
            <button type="submit" disabled={loading}
              style={{ width:'100%', padding:'11px', background:'#7c5cfc', border:'none',
                borderRadius:8, color:'#fff', fontSize:14, fontWeight:600, cursor:'pointer' }}>
              {loading ? '...' : 'Réinitialiser le mot de passe'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (step === 'reset-done') {
    return (
      <div style={saCardStyle}>
        <div style={{ ...saInnerStyle, textAlign:'center' }}>
          <div style={{ fontSize:28, marginBottom:10 }}>✓</div>
          <p style={{ color:'#aaa', fontSize:13, marginBottom:20 }}>Votre mot de passe a été mis à jour.</p>
          <button type="button" onClick={() => { window.history.replaceState({}, '', '/superadmin'); setStep('password'); }}
            style={{ width:'100%', padding:'11px', background:'#7c5cfc', border:'none',
              borderRadius:8, color:'#fff', fontSize:14, fontWeight:600, cursor:'pointer' }}>
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center',
      background:'#0a0b0f', fontFamily:'Inter, sans-serif' }}>
      <div style={{ width:360, padding:32, background:'#13141a', borderRadius:16,
        border:'1px solid #2a2b35', boxShadow:'0 20px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <div style={{ display:'flex', justifyContent:'center', marginBottom:8 }}><BrandIcon size={36}/></div>
          <h1 style={{ color:'#fff', fontSize:20, fontWeight:700, margin:0 }}>Super Admin</h1>
          <p style={{ color:'#666', fontSize:13, marginTop:4 }}>Accès restreint</p>
        </div>
        <form onSubmit={submit}>
          <div style={{ marginBottom:14 }}>
            <label style={{ display:'block', color:'#aaa', fontSize:12, fontWeight:600,
              textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>Email</label>
            <input type="email" value={email} onChange={e=>setEmail(e.target.value)}
              style={{ width:'100%', padding:'10px 12px', background:'#1e1f28',
                border:'1px solid #2a2b35', borderRadius:8, color:'#fff', fontSize:14,
                outline:'none', boxSizing:'border-box' }}/>
          </div>
          <div style={{ marginBottom:20 }}>
            <label style={{ display:'block', color:'#aaa', fontSize:12, fontWeight:600,
              textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>Mot de passe</label>
            <input type="password" value={password} onChange={e=>setPassword(e.target.value)}
              style={{ width:'100%', padding:'10px 12px', background:'#1e1f28',
                border:'1px solid #2a2b35', borderRadius:8, color:'#fff', fontSize:14,
                outline:'none', boxSizing:'border-box' }}/>
            <button type="button" onClick={() => { setForgotEmail(email); setStep('forgot'); }}
              style={{ display:'block', marginTop:6, marginLeft:'auto', background:'none', border:'none',
                color:'#666', fontSize:12, cursor:'pointer', textDecoration:'underline' }}>
              Mot de passe oublié ?
            </button>
          </div>
          <button type="submit" disabled={loading}
            style={{ width:'100%', padding:'11px', background:'#7c5cfc', border:'none',
              borderRadius:8, color:'#fff', fontSize:14, fontWeight:600, cursor:'pointer' }}>
            {loading ? 'Connexion...' : 'Se connecter'}
          </button>
        </form>
        <div style={{ display:'flex', alignItems:'center', gap:10, margin:'18px 0' }}>
          <div style={{ flex:1, height:1, background:'#2a2b35' }} />
          <span style={{ color:'#666', fontSize:11 }}>ou</span>
          <div style={{ flex:1, height:1, background:'#2a2b35' }} />
        </div>
        <button type="button" onClick={submitPasskey} disabled={passkeyLoading}
          style={{ width:'100%', padding:'11px', background:'#1e1f28', border:'1px solid #2a2b35',
            borderRadius:8, color:'#fff', fontSize:14, fontWeight:600, cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
          <KeyRound size={15}/> {passkeyLoading ? 'Connexion...' : 'Se connecter avec une clé d’accès'}
        </button>
      </div>
    </div>
  );
}

// ── 2FA du compte SuperAdmin (dans "Mon profil") ────────────────
function TotpSection() {
  const [status, setStatus]   = useState(null);
  const [busy, setBusy]       = useState(false);
  const [mode, setMode]       = useState('idle'); // idle | enroll | codes | disable
  const [enrollData, setEnrollData] = useState(null);
  const [enrollError, setEnrollError] = useState(null);
  const [backupCodes, setBackupCodes] = useState(null);
  const [disablePassword, setDisablePassword] = useState('');

  const load = () => saApi.get('/superadmin/totp/status').then(r => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startEnroll = async () => {
    setBusy(true);
    try {
      const r = await saApi.post('/superadmin/totp/setup');
      setEnrollData(r.data);
      setMode('enroll');
    } catch { toast.error('Erreur'); }
    finally { setBusy(false); }
  };

  const verifyEnroll = async (code) => {
    setEnrollError(null);
    setBusy(true);
    try {
      const r = await saApi.post('/superadmin/totp/enable', { secret: enrollData.secret, code });
      setBackupCodes(r.data.backupCodes || []);
      setMode('codes');
    } catch(e) { setEnrollError(e.response?.data?.error || 'Code invalide'); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await saApi.post('/superadmin/totp/disable', { password: disablePassword });
      toast.success('2FA désactivée');
      setMode('idle'); setDisablePassword('');
      load();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setBusy(false); }
  };

  if (!status) return null;

  if (mode === 'enroll') {
    return (
      <TotpEnrollForm qrDataUrl={enrollData.qrDataUrl} secret={enrollData.secret}
        onVerify={verifyEnroll} loading={busy} error={enrollError} dark/>
    );
  }
  if (mode === 'codes') {
    return <TotpBackupCodes codes={backupCodes || []} onDone={() => { setMode('idle'); load(); }} dark/>;
  }

  return (
    <div style={{ marginTop:12, padding:'14px 16px', borderRadius:10,
      background:'#1e1f28', border:'1px solid #2a2b35' }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
        <Lock size={14} color={status.totp_enabled ? '#00d2b4' : '#666'}/>
        <span style={{ fontSize:13, fontWeight:700, color:'#fff' }}>Double authentification</span>
        <span style={{ marginLeft:'auto', fontSize:12, color: status.totp_enabled ? '#00d2b4' : '#666' }}>
          {status.totp_enabled ? 'Activée' : 'Désactivée'}
        </span>
      </div>
      {status.totp_enabled ? (
        mode === 'disable' ? (
          <div>
            <input type="password" placeholder="Mot de passe" value={disablePassword}
              onChange={e => setDisablePassword(e.target.value)}
              style={{ width:'100%', padding:'8px 10px', background:'#0a0b0f', border:'1px solid #2a2b35',
                borderRadius:8, color:'#fff', fontSize:13, marginBottom:8, boxSizing:'border-box' }}/>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => setMode('idle')} style={{ flex:1, padding:'8px', borderRadius:8,
                border:'1px solid #2a2b35', background:'#13141a', color:'#888', cursor:'pointer', fontSize:12 }}>
                Annuler
              </button>
              <button onClick={disable} disabled={busy || !disablePassword} style={{ flex:1, padding:'8px',
                borderRadius:8, border:'none', background:'#ff4d6d', color:'#fff', cursor:'pointer', fontSize:12, fontWeight:700 }}>
                Désactiver
              </button>
            </div>
          </div>
        ) : (
          <button onClick={() => setMode('disable')} style={{ padding:'6px 12px', borderRadius:8,
            border:'1px solid rgba(255,77,109,0.3)', background:'rgba(255,77,109,0.08)', color:'#ff4d6d',
            cursor:'pointer', fontSize:12 }}>
            Désactiver la 2FA
          </button>
        )
      ) : (
        <button onClick={startEnroll} disabled={busy} style={{ padding:'6px 12px', borderRadius:8,
          border:'none', background:'#7c5cfc', color:'#fff', cursor:'pointer', fontSize:12, fontWeight:700 }}>
          Activer la 2FA
        </button>
      )}
    </div>
  );
}

// ── Clés d'accès du compte SuperAdmin (dans "Mon profil") ───────
function PasskeysSection() {
  const [passkeys, setPasskeys] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pendingResponse, setPendingResponse] = useState(null); // réponse WebAuthn en attente d'un nom
  const [pendingName, setPendingName] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(null); // id de la clé en attente de confirmation
  const [removing, setRemoving] = useState(false);

  const load = () => saApi.get('/superadmin/passkeys').then(r => setPasskeys(r.data.passkeys)).catch(() => {});
  useEffect(() => { load(); }, []);

  const register = async () => {
    setBusy(true);
    try {
      const { data: options } = await saApi.post('/superadmin/passkeys/register-options');
      const response = await startRegistration(options);
      setPendingName('');
      setPendingResponse(response); // ouvre la modale de nommage
    } catch(e) {
      if (e?.name !== 'NotAllowedError') toast.error(e.response?.data?.error || 'Erreur');
    } finally { setBusy(false); }
  };

  const confirmName = async () => {
    setBusy(true);
    try {
      await saApi.post('/superadmin/passkeys/register-verify', { response: pendingResponse, label: pendingName || undefined });
      toast.success("Clé d'accès ajoutée");
      setPendingResponse(null);
      load();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      await saApi.delete(`/superadmin/passkeys/${confirmRemove}`);
      load();
      setConfirmRemove(null);
    } catch { toast.error('Erreur'); }
    finally { setRemoving(false); }
  };

  if (!passkeys) return null;

  return (
    <div style={{ marginTop:12, padding:'14px 16px', borderRadius:10,
      background:'#1e1f28', border:'1px solid #2a2b35' }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
        <KeyRound size={14} color={passkeys.length ? '#00d2b4' : '#666'}/>
        <span style={{ fontSize:13, fontWeight:700, color:'#fff' }}>Clés d'accès</span>
        <button onClick={register} disabled={busy} style={{ marginLeft:'auto', padding:'6px 12px', borderRadius:8,
          border:'none', background:'#7c5cfc', color:'#fff', cursor:'pointer', fontSize:12, fontWeight:700 }}>
          + Ajouter
        </button>
      </div>
      {passkeys.length === 0 ? (
        <p style={{ color:'#666', fontSize:12, margin:0 }}>Aucune clé d'accès enregistrée</p>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {passkeys.map(p => (
            <div key={p.id} style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:'#ccc' }}>
              <span style={{ flex:1 }}>{p.name || 'Clé sans nom'}</span>
              <span style={{ color:'#666' }}>{new Date(p.created_at).toLocaleDateString('fr-FR')}</span>
              <button onClick={() => setConfirmRemove(p.id)} style={{ background:'none', border:'none',
                color:'#ff4d6d', cursor:'pointer' }}>
                <Trash2 size={13}/>
              </button>
            </div>
          ))}
        </div>
      )}
      {pendingResponse && (
        <SAModal title="Nommer cette clé d'accès" onClose={() => setPendingResponse(null)} onSave={confirmName}>
          <SAField label="Nom (ex: iPhone, YubiKey)" value={pendingName} onChange={setPendingName}/>
        </SAModal>
      )}
      {confirmRemove && (
        <ConfirmModal
          title="Supprimer la clé d'accès"
          message="Cette clé d'accès ne pourra plus être utilisée pour se connecter."
          confirmLabel="Supprimer"
          danger
          loading={removing}
          onConfirm={remove}
          onCancel={() => setConfirmRemove(null)}
        />
      )}
    </div>
  );
}

// StatCard vit maintenant dans components/StatCard.jsx (partagée avec
// l'onglet Système, qui affichait auparavant ses propres tuiles ad-hoc).

// ── Tenants ───────────────────────────────────────────────────
function TenantsTab({ plans }) {
  const [tenants, setTenants] = useState([]);
  const [modal, setModal]     = useState(null);
  const [form, setForm]       = useState({ name:'', slug:'', planId:'', trialDays:14, adminEmail:'', adminFirstName:'', adminLastName:'' });
  const [createdAdmin, setCreatedAdmin] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // tenant en attente de suppression définitive
  const [deleting, setDeleting] = useState(false);

  const load = () => saApi.get('/superadmin/tenants').then(r => setTenants(r.data.tenants));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.planId) return toast.error('Veuillez sélectionner un plan');
    try {
      if (modal === 'new') {
        const r = await saApi.post('/superadmin/tenants', form);
        console.log('Tenant created:', r.data);
        if (r.data.tmpPassword && r.data.admin) {
          setCreatedAdmin({ email: r.data.admin.email, tmpPassword: r.data.tmpPassword });
        } else if (r.data.tmpPassword) {
          setCreatedAdmin({ email: form.adminEmail, tmpPassword: r.data.tmpPassword });
        }
        toast.success('Tenant créé');
      } else {
        await saApi.put(`/superadmin/tenants/${modal.id}`, form);
        toast.success('Tenant mis à jour');
      }
      load(); setModal(null);
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
  };

  const toggle = async (t) => {
    await saApi.put(`/superadmin/tenants/${t.id}`, { isActive: !t.is_active });
    load();
  };

  const toggleTotp = async (t) => {
    try {
      await saApi.put(`/superadmin/tenants/${t.id}/totp`, { enforced: !t.totp_enforced });
      toast.success(!t.totp_enforced ? '2FA imposée sur ce tenant' : '2FA non imposée sur ce tenant');
      load();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
  };

  const deleteTenant = async () => {
    setDeleting(true);
    try {
      await saApi.delete('/superadmin/tenants/' + confirmDelete.id + '?hard=true');
      toast.success('Tenant supprimé définitivement');
      setConfirmDelete(null);
      load();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setDeleting(false); }
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
        <button onClick={() => { setForm({ name:'', slug:'', planId: plans[0]?.id||'', trialDays:14, adminEmail:'', adminFirstName:'', adminLastName:'' }); setCreatedAdmin(null); setModal('new'); }}
          style={btnStyle('#7c5cfc')}>
          <Plus size={14}/> Nouveau tenant
        </button>
      </div>

      <div style={{ background:'#13141a', borderRadius:12, border:'1px solid #2a2b35', overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ borderBottom:'1px solid #2a2b35' }}>
              {['Nom', 'Slug', 'Plan', 'Statut', '2FA', 'Créé le', 'Actions'].map(h => (
                <th key={h} style={{ padding:'12px 16px', textAlign:'left', fontSize:12,
                  fontWeight:600, color:'#666', textTransform:'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tenants.map(t => (
              <tr key={t.id} style={{ borderBottom:'1px solid #1a1b22' }}>
                <td style={{ padding:'12px 16px', color:'#fff', fontWeight:600 }}>{t.name}</td>
                <td style={{ padding:'12px 16px', color:'#888', fontFamily:'monospace', fontSize:13 }}>{t.slug}</td>
                <td style={{ padding:'12px 16px' }}>
                  <span style={{ padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:600,
                    background: t.plan_name==='enterprise'?'rgba(124,92,252,0.15)':t.plan_name==='pro'?'rgba(0,210,180,0.1)':'rgba(255,166,35,0.1)',
                    color: t.plan_name==='enterprise'?'#a387fd':t.plan_name==='pro'?'#00d2b4':'#f5a623' }}>
                    {t.plan_label || '—'}
                  </span>
                </td>
                <td style={{ padding:'12px 16px' }}>
                  {t.is_active
                    ? <span style={{ color:'#00d2b4', fontSize:12 }}>● Actif</span>
                    : <span style={{ color:'#ff4d6d', fontSize:12 }}>● Inactif</span>}
                </td>
                <td style={{ padding:'12px 16px' }}>
                  <button onClick={() => toggleTotp(t)} title="Imposer/lever la 2FA sur ce tenant"
                    style={{ display:'flex', alignItems:'center', gap:5, padding:'3px 10px', borderRadius:20,
                      fontSize:11, fontWeight:600, cursor:'pointer', border:'none',
                      background: t.totp_enforced ? 'rgba(0,210,180,0.15)' : 'rgba(255,255,255,0.06)',
                      color: t.totp_enforced ? '#00d2b4' : '#666' }}>
                    <Lock size={11}/> {t.totp_enforced ? 'Imposée' : 'Libre'}
                  </button>
                </td>
                <td style={{ padding:'12px 16px', color:'#666', fontSize:13 }}>
                  {new Date(t.created_at).toLocaleDateString('fr-FR')}
                </td>
                <td style={{ padding:'12px 16px' }}>
                  <div style={{ display:'flex', gap:8 }}>
                    <button onClick={() => { setForm({ name:t.name, slug:t.slug, planId:t.plan_id, trialDays:'' }); setModal(t); }}
                      style={iconBtn}><Edit2 size={13}/></button>
                    <button onClick={() => toggle(t)} style={iconBtn}>
                      {t.is_active ? <XCircle size={13} color="#ff4d6d"/> : <CheckCircle size={13} color="#00d2b4"/>}
                    </button>
                    <button onClick={() => setConfirmDelete(t)} style={iconBtn} title="Supprimer définitivement">
                      <Trash2 size={13} color="#ff4d6d"/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {createdAdmin && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:1000,
          display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'#13141f', border:'1px solid #2a2b35', borderRadius:16, padding:32, maxWidth:420, width:'100%' }}>
            <div style={{ fontSize:18, fontWeight:800, marginBottom:4, color:'#fff' }}>Tenant créé</div>
            <p style={{ color:'#888', fontSize:13, marginBottom:20 }}>
              Transmettez ces identifiants à l'administrateur. Le mot de passe ne sera plus affiché après fermeture.
            </p>
            <div style={{ background:'#0a0b0f', borderRadius:10, padding:'12px 16px', marginBottom:10 }}>
              <div style={{ fontSize:11, color:'#666', marginBottom:4 }}>Email</div>
              <div style={{ fontFamily:'monospace', fontSize:14, color:'#e8e8f0' }}>{createdAdmin.email}</div>
            </div>
            <div style={{ background:'#0a0b0f', borderRadius:10, padding:'12px 16px', marginBottom:20 }}>
              <div style={{ fontSize:11, color:'#666', marginBottom:4 }}>Mot de passe temporaire</div>
              <div style={{ fontFamily:'monospace', fontSize:20, fontWeight:800, color:'#7c5cfc', letterSpacing:'0.1em' }}>
                {createdAdmin.tmpPassword}
              </div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => { const txt = 'Email : ' + createdAdmin.email + String.fromCharCode(10) + 'Mot de passe : ' + createdAdmin.tmpPassword; navigator.clipboard?.writeText(txt).then(() => toast.success('Copié !')); }}
                style={{ flex:1, padding:'10px', borderRadius:8, border:'1px solid #2a2b35',
                  background:'#1e1f28', color:'#888', cursor:'pointer', fontFamily:'inherit', fontSize:13 }}>
                Copier
              </button>
              <button onClick={() => setCreatedAdmin(null)}
                style={{ flex:2, padding:'10px', borderRadius:8, border:'none',
                  background:'#7c5cfc', color:'#fff', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:700 }}>
                J'ai noté, fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {modal && (
        <SAModal title={modal==='new'?'Nouveau tenant':'Modifier le tenant'} onClose={()=>setModal(null)} onSave={save}>
          <SAField label="Nom" value={form.name} onChange={v=>setForm(p=>({...p,name:v,slug:v.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'').replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'')}))}/>
          <SAField label="Slug (auto)" value={form.slug} onChange={v=>setForm(p=>({...p,slug:v.toLowerCase().replace(/[^a-z0-9-]/g,'')}))} disabled={modal!=='new'}/>
          <div style={{ marginBottom:14 }}>
            <label style={labelStyle}>Plan</label>
            <select value={form.planId} onChange={e=>setForm(p=>({...p,planId:e.target.value}))} style={inputStyle}>
              <option value="">— Choisir —</option>
              {plans.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
          {modal==='new' && <SAField label="Jours d'essai" type="number" value={form.trialDays} onChange={v=>setForm(p=>({...p,trialDays:v}))}/>}
          {modal==='new' && (
            <div style={{ marginTop:12, padding:'12px 14px', borderRadius:8,
              background:'rgba(0,210,180,0.06)', border:'1px solid rgba(0,210,180,0.2)' }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#00d2b4', marginBottom:10 }}>Compte admin du tenant</div>
              <SAField label="Email admin *" type="email" value={form.adminEmail} onChange={v=>setForm(p=>({...p,adminEmail:v}))}/>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                <SAField label="Prénom" value={form.adminFirstName} onChange={v=>setForm(p=>({...p,adminFirstName:v}))}/>
                <SAField label="Nom" value={form.adminLastName} onChange={v=>setForm(p=>({...p,adminLastName:v}))}/>
              </div>
              <p style={{ margin:'6px 0 0', fontSize:11, color:'#666' }}>
                Un mot de passe temporaire sera généré et affiché une seule fois.
              </p>
            </div>
          )}
        </SAModal>
      )}
      {confirmDelete && (
        <ConfirmModal
          title="Suppression définitive"
          message={
            `SUPPRESSION DÉFINITIVE du tenant "${confirmDelete.name}".\n\n` +
            `Toutes les données (réservations, clients, users...) seront effacées.\n` +
            `Cette action est IRRÉVERSIBLE.`
          }
          confirmLabel="Supprimer définitivement"
          danger
          loading={deleting}
          onConfirm={deleteTenant}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

// Remplacement du composant PlansTab dans SuperAdminApp.jsx

// Filet de sécurité uniquement : le libellé affiché vient normalement de
// feature_catalog (colonne `label` renvoyée par GET /superadmin/plans) —
// source unique partagée avec la landing publique. Ce dictionnaire ne sert
// que si une ligne plan_features existe sans entrée catalogue correspondante
// (ne devrait plus arriver depuis que toute nouvelle fonctionnalité passe par
// "+ Nouvelle fonctionnalité" ci-dessous).
const FEATURE_LABELS = {
  bookings:    'Réservations',
  timer:       'Timer salles',
  cash:        'Caisse',
  reports:     'Rapports',
  schedule:    'Planning RH',
  stock:       'Stock & Vente',
  customers:   'Clients',
  multi_site:  'Multi-sites',
  max_rooms:   'Salles max',
  max_sites:   'Sites max',
  max_users:   'Utilisateurs max',
  permissions: 'Droits avancés',
  api_access:  'Accès API',
  white_label: 'White label',
  two_factor_auth: 'Double authentification (2FA)',
};
const featureLabel = (f) => f.label || FEATURE_LABELS[f.feature] || f.feature;
const featureIsLimit = (f) => f.isLimit ?? ['max_rooms','max_sites','max_users'].includes(f.feature);

function PlansTab() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [newFeat, setNewFeat] = useState(null); // { label, isLimit, limitUnitLabel, showOnLanding }
  const [savingFeat, setSavingFeat] = useState(false);

  const load = () => saApi.get('/superadmin/plans').then(r => setPlans(r.data.plans));
  useEffect(() => { load(); }, []);

  const save = async (plan) => {
    try {
      await saApi.put(`/superadmin/plans/${plan.id}`, {
        label: plan.label, priceMonth: plan.price_month, priceYear: plan.price_year,
        features: plan.features,
      });
      toast.success('Plan mis à jour');
      load(); setEditing(null);
    } catch { toast.error('Erreur'); }
  };

  // Ajoute une fonctionnalité entièrement nouvelle : elle est créée dans le
  // catalogue (libellé/type d'affichage) ET ajoutée — désactivée par défaut —
  // à TOUS les plans. Il ne reste qu'à l'activer par plan via "Modifier".
  // Aucune modification de code ni de la landing n'est nécessaire.
  const createFeature = async () => {
    if (!newFeat?.label?.trim()) return toast.error('Libellé requis');
    setSavingFeat(true);
    try {
      await saApi.post('/superadmin/feature-catalog', {
        label: newFeat.label.trim(),
        isLimit: !!newFeat.isLimit,
        limitUnitLabel: newFeat.isLimit ? (newFeat.limitUnitLabel || null) : null,
        showOnLanding: newFeat.showOnLanding !== false,
      });
      toast.success(`"${newFeat.label}" ajoutée à tous les plans (désactivée) — activez-la par plan ci-dessous`);
      setNewFeat(null);
      load();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setSavingFeat(false); }
  };

  const PLAN_COLORS = {
    starter:    '#f5a623',
    pro:        '#00d2b4',
    enterprise: '#a387fd',
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
        <button onClick={() => setNewFeat({ label:'', isLimit:false, limitUnitLabel:'', showOnLanding:true })}
          style={btnStyle('#7c5cfc')}>
          <Plus size={14}/> Nouvelle fonctionnalité
        </button>
      </div>

    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))', gap:16 }}>
      {plans.map(plan => {
        const color = PLAN_COLORS[plan.name] || '#888';
        return (
          <div key={plan.id} style={{ background:'#13141a', borderRadius:12,
            border:`1px solid #2a2b35`, padding:20, borderTop:`3px solid ${color}` }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <div>
                <div style={{ color:'#fff', fontWeight:700, fontSize:17 }}>{plan.label}</div>
                <div style={{ color:'#555', fontSize:12, marginTop:2 }}>{plan.name}</div>
              </div>
              <button onClick={() => setEditing(JSON.parse(JSON.stringify(plan)))} style={iconBtn}>
                <Edit2 size={13}/>
              </button>
            </div>

            {/* Prix */}
            <div style={{ display:'flex', gap:10, marginBottom:16 }}>
              <div style={{ flex:1, background:'#1e1f28', borderRadius:8, padding:'10px 12px', textAlign:'center' }}>
                <div style={{ color:'#555', fontSize:11, marginBottom:2 }}>Mensuel</div>
                <div style={{ color:'#fff', fontWeight:700, fontSize:18 }}>{plan.price_month} €</div>
              </div>
              <div style={{ flex:1, background:'#1e1f28', borderRadius:8, padding:'10px 12px', textAlign:'center' }}>
                <div style={{ color:'#555', fontSize:11, marginBottom:2 }}>Annuel</div>
                <div style={{ color:'#fff', fontWeight:700, fontSize:18 }}>{plan.price_year} €</div>
                <div style={{ color:'#555', fontSize:10 }}>
                  {plan.price_year > 0 ? `${((1 - plan.price_year/(plan.price_month*12))*100).toFixed(0)}% off` : ''}
                </div>
              </div>
            </div>

            {/* Features */}
            <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
              {(plan.features||[]).map(f => (
                <div key={f.feature} style={{ display:'flex', justifyContent:'space-between',
                  alignItems:'center', padding:'5px 8px', borderRadius:6,
                  background: f.enabled ? 'rgba(0,210,180,0.05)' : 'transparent',
                  fontSize:13 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                    <span style={{ color: f.enabled ? '#00d2b4' : '#444', fontSize:14 }}>
                      {f.enabled ? '✓' : '✗'}
                    </span>
                    <span style={{ color: f.enabled ? '#ccc' : '#555' }}>
                      {featureLabel(f)}
                    </span>
                  </div>
                  {f.limit_value && (
                    <span style={{ fontSize:12, color: color, fontWeight:700 }}>
                      max {f.limit_value}
                    </span>
                  )}
                  {f.enabled && !f.limit_value && featureIsLimit(f) && (
                    <span style={{ fontSize:11, color:'#00d2b4' }}>illimité</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>

      {editing && (
        <SAModal title={`Modifier — ${editing.label}`} onClose={()=>setEditing(null)} onSave={()=>save(editing)}>
          <SAField label="Label" value={editing.label} onChange={v=>setEditing(p=>({...p,label:v}))}/>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <SAField label="Prix/mois (€)" type="number" value={editing.price_month}
              onChange={v=>setEditing(p=>({...p,price_month:v}))}/>
            <SAField label="Prix/an (€)" type="number" value={editing.price_year}
              onChange={v=>setEditing(p=>({...p,price_year:v}))}/>
          </div>
          <div style={{ marginTop:12 }}>
            <label style={labelStyle}>Features</label>
            {(editing.features||[]).map((f,i) => (
              <div key={f.feature} style={{ display:'flex', alignItems:'center', gap:8,
                padding:'8px 10px', background:'#1e1f28', borderRadius:7, marginBottom:4 }}>
                {/* Toggle activé/désactivé */}
                <button onClick={() => setEditing(p=>({...p,
                  features:p.features.map((ff,j)=>j===i?{...ff,enabled:!ff.enabled}:ff)}))}
                  style={{ background:'none', border:'none', cursor:'pointer', fontSize:16,
                    color:f.enabled?'#00d2b4':'#555', width:20 }}>
                  {f.enabled ? '✓' : '✗'}
                </button>
                {/* Label */}
                <span style={{ flex:1, color:'#aaa', fontSize:13 }}>
                  {featureLabel(f)}
                </span>
                {/* Limite si applicable */}
                {featureIsLimit(f) && f.enabled && (
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <span style={{ fontSize:11, color:'#555' }}>max</span>
                    <input type="number" min="0"
                      value={f.limit_value || ''}
                      placeholder="∞"
                      onChange={e => setEditing(p=>({...p,
                        features:p.features.map((ff,j)=>j===i
                          ? {...ff, limit_value: e.target.value ? parseInt(e.target.value) : null}
                          : ff)}))}
                      style={{ ...inputStyle, width:60, padding:'4px 8px', fontSize:13 }}/>
                  </div>
                )}
              </div>
            ))}
          </div>
        </SAModal>
      )}

      {newFeat && (
        <SAModal title="Nouvelle fonctionnalité" onClose={() => setNewFeat(null)} onSave={createFeature} saving={savingFeat}>
          <SAField label="Libellé (ex: Commande à table, Happy Hours…)" value={newFeat.label}
            onChange={v => setNewFeat(p => ({ ...p, label:v }))}/>
          <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:12, cursor:'pointer', fontSize:13, color:'#aaa' }}>
            <input type="checkbox" checked={newFeat.isLimit}
              onChange={e => setNewFeat(p => ({ ...p, isLimit: e.target.checked }))}/>
            C'est une limite numérique (comme "Salles" ou "Utilisateurs") plutôt qu'un simple on/off
          </label>
          {newFeat.isLimit && (
            <SAField label="Unité affichée (ex: salles, sites, exports/mois…)" value={newFeat.limitUnitLabel}
              onChange={v => setNewFeat(p => ({ ...p, limitUnitLabel:v }))}/>
          )}
          <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:12, cursor:'pointer', fontSize:13, color:'#aaa' }}>
            <input type="checkbox" checked={newFeat.showOnLanding}
              onChange={e => setNewFeat(p => ({ ...p, showOnLanding: e.target.checked }))}/>
            Afficher sur la landing publique (décochez pour une fonctionnalité interne, ex: sécurité)
          </label>
          <p style={{ fontSize:12, color:'#666', marginTop:12 }}>
            Sera ajoutée — désactivée — à tous les plans existants. Activez-la ensuite par plan via "Modifier".
          </p>
        </SAModal>
      )}
    </div>
  );
}


// ── Modale de saisie du code support (connexion "en tant que") ─────────
// Même UX que le code TOTP (2FA) : 6 cases, avance automatique, collage,
// validation automatique dès le 6e chiffre — plus de window.prompt().
function ImpersonateCodeModal({ user, onSubmit, onClose, loading, error }) {
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);
  const submittedRef = useRef(false);

  useEffect(() => { inputRefs.current[0]?.focus(); }, []);
  useEffect(() => {
    if (error) {
      setDigits(['', '', '', '', '', '']);
      submittedRef.current = false;
      inputRefs.current[0]?.focus();
    }
  }, [error]);

  const submitCode = (code) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    onSubmit(code);
  };

  const handleChange = (i, val) => {
    const v = val.replace(/[^0-9]/g, '').slice(-1);
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    if (v && i < 5) inputRefs.current[i + 1]?.focus();
    const joined = next.join('');
    if (joined.length === 6 && next.every(d => d !== '')) submitCode(joined);
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) inputRefs.current[i - 1]?.focus();
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 6);
    if (text.length === 6) {
      e.preventDefault();
      setDigits(text.split(''));
      submitCode(text);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 380, textAlign: 'center' }}>
        <h3 style={{ marginTop: 0 }}>Code support</h3>
        <p style={{ color: 'var(--text-3)', fontSize: 13, lineHeight: 1.6 }}>
          Code à 6 chiffres communiqué oralement par <strong>{user.email}</strong>, généré
          depuis son propre tableau de bord (valable 5 minutes).
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', margin: '20px 0' }}>
          {digits.map((d, i) => (
            <input key={i} ref={el => inputRefs.current[i] = el}
              type="text" inputMode="numeric" maxLength={1} value={d}
              disabled={loading}
              onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              onPaste={handlePaste}
              onFocus={e => e.target.select()}
              style={{ width: 44, height: 52, textAlign: 'center', fontSize: 20, fontWeight: 700,
                background: '#13141a', border: '1px solid #2a2b35', borderRadius: 10, color: '#fff',
                outline: 'none' }}/>
          ))}
        </div>
        {loading && <div style={{ color: 'var(--text-3)', fontSize: 12, marginBottom: 12 }}>Vérification…</div>}
        {error && <div style={{ color: '#ff4d6d', fontSize: 13, marginBottom: 12 }}>{error}</div>}
        <button className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }}
          onClick={onClose}>Annuler</button>
      </div>
    </div>
  );
}

// ── Utilisateurs (cross-tenant) ──────────────────────────────
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [impersonateTarget, setImpersonateTarget] = useState(null); // user en attente du code
  const [impersonateLoading, setImpersonateLoading] = useState(false);
  const [impersonateError, setImpersonateError] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'disableTotp'|'sendReset', user }
  const [confirmBusy, setConfirmBusy] = useState(false);

  const load = () => {
    setLoading(true);
    saApi.get('/superadmin/users').then(r => setUsers(r.data.users)).catch(() => toast.error('Erreur')).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const toggleBlock = async (u) => {
    setBusyId(u.id);
    try {
      await saApi.put(`/superadmin/users/${u.id}/block`, { blocked: u.is_active });
      toast.success(u.is_active ? 'Compte bloqué' : 'Compte débloqué');
      load();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setBusyId(null); }
  };

  const disableTotp = async (u) => {
    setBusyId(u.id);
    try {
      await saApi.put(`/superadmin/users/${u.id}/disable-totp`);
      toast.success('2FA désactivée pour cet utilisateur');
      load();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setBusyId(null); }
  };

  // Pratique quand le support n'a pas (ou plus) le mot de passe du client,
  // par ex. pour reproduire un bug à sa place — envoie le même lien que
  // "mot de passe oublié" côté app tenant.
  const sendReset = async (u) => {
    setBusyId(u.id);
    try {
      await saApi.post(`/superadmin/users/${u.id}/send-reset`);
      toast.success('Lien de réinitialisation envoyé');
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setBusyId(null); }
  };

  const runConfirmedAction = async () => {
    if (!confirmAction) return;
    setConfirmBusy(true);
    try {
      if (confirmAction.type === 'disableTotp') await disableTotp(confirmAction.user);
      if (confirmAction.type === 'sendReset') await sendReset(confirmAction.user);
    } finally {
      setConfirmBusy(false);
      setConfirmAction(null);
    }
  };

  // Nécessite le code à 6 chiffres généré par l'UTILISATEUR sur son propre
  // tableau de bord ("Code support") — jamais son mot de passe. Ouvre un
  // nouvel onglet déjà connecté à sa place. La saisie se fait via une modale
  // (ImpersonateCodeModal), plus de window.prompt().
  const submitImpersonateCode = async (code) => {
    setImpersonateLoading(true);
    setImpersonateError(null);
    try {
      // loginUrl est construite côté backend (process.env.FRONTEND_URL) :
      // le SuperAdmin et l'app tenant peuvent être sur des domaines
      // différents, la dériver côté frontend ouvrait sur le mauvais domaine.
      const r = await saApi.post(`/superadmin/users/${impersonateTarget.id}/impersonate`, { code });
      window.open(r.data.loginUrl, '_blank');
      setImpersonateTarget(null);
    } catch(e) {
      setImpersonateError(e.response?.data?.error || 'Code invalide ou expiré');
    } finally {
      setImpersonateLoading(false);
    }
  };

  const filtered = users.filter(u => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (u.email||'').toLowerCase().includes(s)
      || (u.first_name||'').toLowerCase().includes(s)
      || (u.last_name||'').toLowerCase().includes(s)
      || (u.tenant_name||'').toLowerCase().includes(s);
  });

  return (
    <div>
      <div style={{ marginBottom:16 }}>
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Rechercher par nom, email, tenant…"
          style={{ ...inputStyle, maxWidth:360 }}/>
      </div>
      {loading ? (
        <div style={{ textAlign:'center', padding:40, color:'#555' }}>Chargement…</div>
      ) : (
        <div style={{ background:'#13141a', borderRadius:12, border:'1px solid #2a2b35', overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ borderBottom:'1px solid #2a2b35' }}>
                {['Utilisateur', 'Tenant', 'Rôle', 'Statut', '2FA', 'Actions'].map(h => (
                  <th key={h} style={{ padding:'12px 16px', textAlign:'left', fontSize:12,
                    fontWeight:600, color:'#666', textTransform:'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id} style={{ borderBottom:'1px solid #1a1b22' }}>
                  <td style={{ padding:'12px 16px' }}>
                    <div style={{ color:'#fff', fontWeight:600 }}>{u.first_name} {u.last_name}</div>
                    <div style={{ color:'#666', fontSize:12 }}>{u.email}</div>
                  </td>
                  <td style={{ padding:'12px 16px', color:'#888', fontSize:13 }}>
                    {u.tenant_name || <span style={{ color:'#444' }}>—</span>}
                  </td>
                  <td style={{ padding:'12px 16px', color:'#888', fontSize:13 }}>{u.role}</td>
                  <td style={{ padding:'12px 16px' }}>
                    {u.is_active
                      ? <span style={{ color:'#00d2b4', fontSize:12 }}>● Actif</span>
                      : <span style={{ color:'#ff4d6d', fontSize:12 }}>● Bloqué</span>}
                  </td>
                  <td style={{ padding:'12px 16px' }}>
                    <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:12,
                      color: u.totp_enabled ? '#00d2b4' : '#666' }}>
                      <Lock size={11}/> {u.totp_enabled ? 'Activée' : 'Désactivée'}
                    </span>
                  </td>
                  <td style={{ padding:'12px 16px' }}>
                    <div style={{ display:'flex', gap:8 }}>
                      <button onClick={() => toggleBlock(u)} disabled={busyId===u.id} style={iconBtn}
                        title={u.is_active ? 'Bloquer le compte' : 'Débloquer le compte'}>
                        {u.is_active ? <XCircle size={13} color="#ff4d6d"/> : <CheckCircle size={13} color="#00d2b4"/>}
                      </button>
                      {u.totp_enabled && (
                        <button onClick={() => setConfirmAction({ type:'disableTotp', user:u })} disabled={busyId===u.id} style={iconBtn}
                          title="Désactiver la 2FA">
                          <Lock size={13} color="#f5a623"/>
                        </button>
                      )}
                      <button onClick={() => setConfirmAction({ type:'sendReset', user:u })} disabled={busyId===u.id} style={iconBtn}
                        title="Envoyer un lien de réinitialisation de mot de passe">
                        <Mail size={13} color="#7c5cfc"/>
                      </button>
                      <button onClick={() => { setImpersonateTarget(u); setImpersonateError(null); }}
                        disabled={busyId===u.id} style={iconBtn}
                        title="Se connecter en tant que (nécessite le code support de l'utilisateur)">
                        <LogIn size={13} color="#00d2b4"/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} style={{ padding:30, textAlign:'center', color:'#555' }}>Aucun utilisateur</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {impersonateTarget && (
        <ImpersonateCodeModal
          user={impersonateTarget}
          loading={impersonateLoading}
          error={impersonateError}
          onSubmit={submitImpersonateCode}
          onClose={() => setImpersonateTarget(null)}
        />
      )}
      {confirmAction && (
        <ConfirmModal
          title={confirmAction.type === 'disableTotp' ? 'Désactiver la 2FA' : 'Envoyer un lien de réinitialisation'}
          message={
            confirmAction.type === 'disableTotp'
              ? `Désactiver la 2FA de ${confirmAction.user.email} ?`
              : `Envoyer un lien de réinitialisation de mot de passe à ${confirmAction.user.email} ?`
          }
          confirmLabel={confirmAction.type === 'disableTotp' ? 'Désactiver' : 'Envoyer'}
          danger={confirmAction.type === 'disableTotp'}
          loading={confirmBusy}
          onConfirm={runConfirmedAction}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
}

// ── Super Admins ──────────────────────────────────────────────
function AdminsTab() {
  const [admins, setAdmins] = useState([]);
  const [modal, setModal]   = useState(false);
  const [form, setForm]     = useState({ email:'', password:'', firstName:'', lastName:'' });
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  const load = () => saApi.get('/superadmin/admins').then(r => setAdmins(r.data.admins));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await saApi.post('/superadmin/admins', form);
      toast.success('Admin créé'); load(); setModal(false);
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      await saApi.delete(`/superadmin/admins/${confirmRemove.id}`);
      load();
      setConfirmRemove(null);
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setRemoving(false); }
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
        <button onClick={() => { setForm({ email:'', password:'', firstName:'', lastName:'' }); setModal(true); }}
          style={btnStyle('#7c5cfc')}><Plus size={14}/> Nouvel admin</button>
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {admins.map(a => (
          <div key={a.id} style={{ display:'flex', alignItems:'center', gap:14,
            padding:'14px 18px', background:'#13141a', borderRadius:10, border:'1px solid #2a2b35' }}>
            <div style={{ width:36, height:36, borderRadius:'50%', background:'linear-gradient(135deg,#7c5cfc,#00d2b4)',
              display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, color:'#fff' }}>
              {a.first_name?.[0]}{a.last_name?.[0]}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ color:'#fff', fontWeight:600 }}>{a.first_name} {a.last_name}</div>
              <div style={{ color:'#666', fontSize:13 }}>{a.email}</div>
            </div>
            <div style={{ fontSize:12, color: a.is_active?'#00d2b4':'#ff4d6d' }}>
              {a.is_active ? '● Actif' : '● Inactif'}
            </div>
            {a.last_login_at && (
              <div style={{ fontSize:12, color:'#555' }}>
                Dernière connexion : {new Date(a.last_login_at).toLocaleDateString('fr-FR')}
              </div>
            )}
            <button onClick={() => setConfirmRemove(a)} style={iconBtn}><Trash2 size={13} color="#ff4d6d"/></button>
          </div>
        ))}
      </div>
      {modal && (
        <SAModal title="Nouvel admin" onClose={()=>setModal(false)} onSave={create}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <SAField label="Prénom" value={form.firstName} onChange={v=>setForm(p=>({...p,firstName:v}))}/>
            <SAField label="Nom" value={form.lastName} onChange={v=>setForm(p=>({...p,lastName:v}))}/>
          </div>
          <SAField label="Email" type="email" value={form.email} onChange={v=>setForm(p=>({...p,email:v}))}/>
          <SAField label="Mot de passe" type="password" value={form.password} onChange={v=>setForm(p=>({...p,password:v}))}/>
        </SAModal>
      )}
      {confirmRemove && (
        <ConfirmModal
          title="Désactiver cet admin"
          message={`Désactiver l'accès admin de ${confirmRemove.email} ?`}
          confirmLabel="Désactiver"
          danger
          loading={removing}
          onConfirm={remove}
          onCancel={() => setConfirmRemove(null)}
        />
      )}
    </div>
  );
}

// ── Helpers UI ────────────────────────────────────────────────
const labelStyle = { display:'block', color:'#888', fontSize:12, fontWeight:600,
  textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:5 };
const inputStyle = { width:'100%', padding:'9px 12px', background:'#1e1f28',
  border:'1px solid #2a2b35', borderRadius:8, color:'#fff', fontSize:14,
  outline:'none', boxSizing:'border-box', fontFamily:'inherit' };
const iconBtn = { background:'#1e1f28', border:'1px solid #2a2b35', borderRadius:6,
  padding:'5px 8px', cursor:'pointer', color:'#aaa', display:'flex', alignItems:'center' };
const btnStyle = (bg) => ({
  display:'flex', alignItems:'center', gap:6, padding:'8px 16px',
  background:bg, border:'none', borderRadius:8, color:'#fff',
  fontSize:13, fontWeight:600, cursor:'pointer',
});

function SAField({ label, value, onChange, type='text', disabled=false }) {
  return (
    <div style={{ marginBottom:14 }}>
      <label style={labelStyle}>{label}</label>
      <input type={type} value={value} onChange={e=>onChange(e.target.value)} disabled={disabled}
        style={{ ...inputStyle, opacity: disabled ? 0.5 : 1 }}/>
    </div>
  );
}

// Classe standard modal-overlay/modal (au lieu de styles ad hoc) : reçoit
// gratuitement Entrée = valider / Échap = annuler via <ModalHotkeys/>.
function SAModal({ title, children, onClose, onSave }) {
  return (
    <div className="modal-overlay" style={{ zIndex:100 }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{ background:'#13141a', border:'1px solid #2a2b35',
        maxWidth:520 }}>
        <h3 style={{ color:'#fff', fontSize:16, fontWeight:700, marginBottom:20, marginTop:0 }}>{title}</h3>
        {children}
        <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:20 }}>
          <button onClick={onClose} style={{ padding:'8px 16px', background:'#2a2b35', border:'none',
            borderRadius:8, color:'#aaa', cursor:'pointer', fontSize:13 }}>Annuler</button>
          <button onClick={onSave} style={btnStyle('#7c5cfc')}>Enregistrer</button>
        </div>
      </div>
    </div>
  );
}


// ── Demandes de changement de plan ───────────────────────────
function RequestsTab({ onAction }) {
  const [requests, setRequests] = useState([]);
  const load = () => saApi.get('/superadmin/plan-requests').then(r => setRequests(r.data.requests||[]));
  useEffect(() => { load(); }, []);

  const review = async (id, status) => {
    try {
      await saApi.put(`/superadmin/plan-requests/${id}`, { status });
      toast.success(status === 'approved' ? 'Plan mis à jour' : 'Demande refusée');
      load(); onAction();
    } catch { toast.error('Erreur'); }
  };

  const STATUS_COLORS = { pending:'#f5a623', approved:'#00d2b4', rejected:'#ff4d6d' };
  const STATUS_LABELS = { pending:'En attente', approved:'Approuvé', rejected:'Refusé' };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
      {requests.length === 0 && (
        <div style={{ textAlign:'center', padding:40, color:'#555' }}>Aucune demande</div>
      )}
      {requests.map(r => (
        <div key={r.id} style={{ background:'#13141a', borderRadius:10, border:'1px solid #2a2b35',
          padding:'16px 20px', display:'flex', alignItems:'center', gap:16 }}>
          <div style={{ flex:1 }}>
            <div style={{ color:'#fff', fontWeight:600, marginBottom:4 }}>
              {r.tenant_name} <span style={{ color:'#555', fontWeight:400 }}>({r.slug})</span>
            </div>
            <div style={{ fontSize:13, color:'#888' }}>
              {r.from_plan_label || '—'} → <strong style={{ color:'#a387fd' }}>{r.to_plan_label}</strong>
            </div>
            {r.message && <div style={{ fontSize:12, color:'#666', marginTop:4, fontStyle:'italic' }}>"{r.message}"</div>}
            <div style={{ fontSize:11, color:'#555', marginTop:4 }}>
              Par {r.first_name} {r.last_name} · {new Date(r.created_at).toLocaleDateString('fr-FR')}
            </div>
          </div>
          <span style={{ padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:600,
            background:`${STATUS_COLORS[r.status]}22`, color:STATUS_COLORS[r.status] }}>
            {STATUS_LABELS[r.status]}
          </span>
          {r.status === 'pending' && (
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => review(r.id,'approved')} style={btnStyle('#00d2b4')}>
                <CheckCircle size={13}/> Approuver
              </button>
              <button onClick={() => review(r.id,'rejected')} style={btnStyle('#ff4d6d')}>
                <XCircle size={13}/> Refuser
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Mon profil ────────────────────────────────────────────────
function ProfileModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ email:'', firstName:'', lastName:'', currentPassword:'', password:'', passwordConfirm:'' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    saApi.get('/superadmin/me').then(r => {
      setForm(p => ({ ...p, email: r.data.admin.email, firstName: r.data.admin.first_name||'', lastName: r.data.admin.last_name||'' }));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const save = async () => {
    if (form.password && form.password !== form.passwordConfirm) {
      toast.error('Les mots de passe ne correspondent pas'); return;
    }
    if (form.password && !form.currentPassword) {
      toast.error('Mot de passe actuel requis pour le changer'); return;
    }
    try {
      const payload = { email: form.email, firstName: form.firstName, lastName: form.lastName };
      if (form.password) { payload.password = form.password; payload.currentPassword = form.currentPassword; }
      await saApi.put('/superadmin/me', payload);
      toast.success('Profil mis à jour');
      onSaved?.(form.email);
      onClose();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
  };

  if (loading) return null;

  return (
    <SAModal title="Mon profil" onClose={onClose} onSave={save}>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <SAField label="Prénom" value={form.firstName} onChange={v=>setForm(p=>({...p,firstName:v}))}/>
        <SAField label="Nom" value={form.lastName} onChange={v=>setForm(p=>({...p,lastName:v}))}/>
      </div>
      <SAField label="Email" type="email" value={form.email} onChange={v=>setForm(p=>({...p,email:v}))}/>
      <div style={{ marginTop:12, padding:'12px 14px', borderRadius:8,
        background:'rgba(124,92,252,0.06)', border:'1px solid rgba(124,92,252,0.2)' }}>
        <div style={{ fontSize:12, fontWeight:700, color:'#a387fd', marginBottom:10 }}>Changer le mot de passe</div>
        <SAField label="Mot de passe actuel" type="password" value={form.currentPassword} onChange={v=>setForm(p=>({...p,currentPassword:v}))}/>
        <SAField label="Nouveau mot de passe" type="password" value={form.password} onChange={v=>setForm(p=>({...p,password:v}))}/>
        <SAField label="Confirmer le nouveau mot de passe" type="password" value={form.passwordConfirm} onChange={v=>setForm(p=>({...p,passwordConfirm:v}))}/>
        <p style={{ margin:'4px 0 0', fontSize:11, color:'#666' }}>Laisser vide pour ne pas changer le mot de passe.</p>
      </div>
      <TotpSection/>
      <PasskeysSection/>
    </SAModal>
  );
}

// ── App principale ────────────────────────────────────────────
export default function SuperAdminApp() {
  const [admin, setAdmin] = useState(null);
  const [tab, setTab]     = useState('tenants');
  const [stats, setStats] = useState(null);
  const [plans, setPlans] = useState([]);
  const [showProfile, setShowProfile] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('sa_token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp * 1000 > Date.now()) {
          setAdmin({ email: payload.email });
          loadData();
        } else { localStorage.removeItem('sa_token'); }
      } catch { localStorage.removeItem('sa_token'); }
    }
  }, []);

  const loadData = useCallback(() => {
    saApi.get('/superadmin/stats').then(r => setStats(r.data)).catch(()=>{});
    saApi.get('/superadmin/plans').then(r => setPlans(r.data.plans)).catch(()=>{});
  }, []);

  const handleLogin = (a) => { setAdmin(a); loadData(); };

  const logout = () => { localStorage.removeItem('sa_token'); setAdmin(null); };

  if (!admin) return <><Toaster/><SALogin onLogin={handleLogin}/></>;

  const TABS = [
    { id:'tenants',  label:'Tenants',  icon:Building2 },
    { id:'plans',    label:'Plans',    icon:Package },
    { id:'requests', label:`Demandes${stats?.pendingRequests>0?' ('+stats.pendingRequests+')':''}`, icon:Shield },
    { id:'users',       label:'Utilisateurs', icon:Users },
    { id:'admins',      label:'Admins',      icon:Shield },
    { id:'migrations',  label:'Migrations',  icon:Database },
    { id:'system',       label:'Système',     icon:Settings },
  ];

  return (
    <div style={{ minHeight:'100vh', background:'#0a0b0f', fontFamily:'Inter, sans-serif', color:'#fff' }}>
      <Toaster/>
      {/* Header */}
      <div style={{ background:'#13141a', borderBottom:'1px solid #2a2b35',
        padding:'0 24px', display:'flex', alignItems:'center', height:56 }}>
        <span style={{ fontWeight:800, fontSize:16, display:'flex', alignItems:'center', gap:8 }}>
          <BrandIcon size={20}/> Super Admin
        </span>
        <span style={{ flex:1 }}/>
        <a href="/app" target="_blank" rel="noreferrer"
          style={{ color:'#666', textDecoration:'none', display:'flex', alignItems:'center',
            gap:6, fontSize:13, marginRight:16 }}>
          <ArrowRight size={14}/> Aller sur l'app
        </a>
        <span style={{ fontSize:13, color:'#666', marginRight:16 }}>{admin.email}</span>
        <button onClick={() => setShowProfile(true)}
          style={{ background:'none', border:'none', cursor:'pointer', color:'#666', display:'flex',
            alignItems:'center', gap:6, fontSize:13, marginRight:16 }}>
          <User size={15}/> Mon profil
        </button>
        <button onClick={logout} style={{ background:'none', border:'none', cursor:'pointer', color:'#666' }}>
          <LogOut size={16}/>
        </button>
      </div>

      {showProfile && (
        <ProfileModal
          onClose={() => setShowProfile(false)}
          onSaved={(email) => setAdmin(a => ({ ...a, email }))}
        />
      )}

      <div style={{ maxWidth:1200, margin:'0 auto', padding:24 }}>
        {/* Stats — Revenu & tenants */}
        {stats && (
          <>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(170px,1fr))', gap:12, marginBottom:12 }}>
              <StatCard label="Tenants actifs" value={stats.tenantCount} icon={Building2} color="#7c5cfc"/>
              <StatCard label="Nouveaux (30j)" value={stats.newTenants30d} icon={TrendingUp} color="#22d3a0"/>
              <StatCard label="Essais en cours" value={stats.trialCount} icon={Clock} color="#f5a623"/>
              <StatCard label="MRR" value={`${stats.mrr?.toFixed(0)} €`} icon={Wallet} color="#22d3a0"/>
              <StatCard label="ARR" value={`${(stats.mrr*12)?.toFixed(0)} €`} icon={Wallet} color="#00d2b4"/>
            </div>
            {/* Usage & risques — indicateurs secondaires, moins financiers */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(170px,1fr))', gap:12, marginBottom:24 }}>
              <StatCard label="Utilisateurs actifs" value={stats.activeUsers} icon={Users} color="#7c5cfc"/>
              <StatCard label="Réservations (mois)" value={stats.bookingsThisMonth} icon={Calendar} color="#00d2b4"/>
              <StatCard label="Essais < 7j" value={stats.trialEndingSoon} icon={AlertTriangle} color={stats.trialEndingSoon>0?"#f5a623":"#666"}/>
              <StatCard label="Tenants désactivés" value={stats.inactiveTenants} icon={UserX} color={stats.inactiveTenants>0?"#ff4d6d":"#666"}/>
              <StatCard label="Demandes en attente" value={stats.pendingRequests} icon={Shield} color={stats.pendingRequests>0?"#ff4d6d":"#666"}/>
              <StatCard label="Super Admins" value={stats.adminCount} icon={Shield} color="#00d2b4"/>
            </div>
          </>
        )}

        {/* Tabs */}
        <div style={{ display:'flex', gap:4, marginBottom:20 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ display:'flex', alignItems:'center', gap:7, padding:'8px 16px',
                borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight:600,
                background: tab===t.id ? '#7c5cfc' : '#1e1f28',
                color: tab===t.id ? '#fff' : '#888' }}>
              <t.icon size={14}/> {t.label}
            </button>
          ))}
          <button onClick={loadData} style={{ marginLeft:'auto', ...iconBtn }}>
            <RefreshCw size={14}/>
          </button>
        </div>

        {tab==='tenants'  && <TenantsTab plans={plans}/>}
        {tab==='plans'    && <PlansTab/>}
        {tab==='requests' && <RequestsTab onAction={loadData}/>}
        {tab==='users'       && <UsersTab/>}
        {tab==='admins'      && <AdminsTab/>}
        {tab==='migrations'  && <SuperAdminMigrations/>}
        {tab==='system'       && <SuperAdminSystem/>}
      </div>
    </div>
  );
}
